import React, { useState, useEffect } from "react";
import { Menu } from "antd";
import type { MenuProps } from "antd";
import { useNavigate, useLocation } from "react-router-dom";
import style from "./sider.module.css";
import Logo from "../../Images/ahlibank.png";
import { MdDashboard, MdCampaign, MdOutlineUploadFile, MdSettings, MdNotifications, MdBusiness } from "react-icons/md";
import { VscUngroupByRefType } from "react-icons/vsc";
import { BsFileEarmarkRuled } from "react-icons/bs";
import { AiOutlineTransaction } from "react-icons/ai";
import { TbReport } from "react-icons/tb";
import { GiPodiumWinner } from "react-icons/gi";
import { FaUserCircle } from "react-icons/fa";
import { AiOutlineAudit } from "react-icons/ai";
import { hasPermission } from '../../utils/premission';
import { PERMISSIONS } from '../../utils/constant/permissions';
import { useSidebar } from '../../context/SidebarContext';
import { GrConfigure } from "react-icons/gr";



type MenuItem = Required<MenuProps>["items"][number];

const Sider: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const currentPath = location.pathname;
  const { collapsed, isMobile, closeSidebar } = useSidebar();

  const [openKeys, setOpenKeys] = useState<string[]>([]);

  useEffect(() => {
    if (currentPath.startsWith("/upload") || currentPath.startsWith("/screenmaker")) {
      setOpenKeys(["Upload"]);
    } else if (currentPath.startsWith("/Campaigns") || currentPath.startsWith("/Transfer")) {
      setOpenKeys(["/Campaigns"]);
    } else if (
      currentPath.startsWith("/adconfig") ||
      currentPath.startsWith("/notifyconfig") ||
      currentPath.startsWith("/password-policy") ||
      currentPath.startsWith("/smtp-config")
    ) {
      setOpenKeys(["configuration"]);
    } else if (currentPath.startsWith("/transaction")) {
      setOpenKeys(["Transaction"]);
    } else {
      setOpenKeys([]);
    }
  }, [currentPath]);

  const onClick: MenuProps["onClick"] = (e) => {
    if (e.key === "logout") {
      console.log("Logging out...");
      return;
    }
    // Don't navigate on submenu parent keys
    const parentKeys = ["Upload", "/Campaigns", "Transaction", "configuration"];
    if (parentKeys.includes(e.key)) return;

    navigate(e.key);
    if (isMobile) closeSidebar();
  };



  const filterMenuItems = (items: any[]) =>
    items.filter(Boolean);

  const items: MenuItem[] = filterMenuItems([

    {
      key: "/dashboard",
      label: "Dashboard",
      icon: <MdDashboard style={{ fontSize: 24 }} />,
    },

    hasPermission(PERMISSIONS.CAMPAIGN_VIEW) && {
      key: "/campaigns",
      label: "Campaigns",
      icon: <MdCampaign style={{ fontSize: 24 }} />,
    },

    hasPermission(PERMISSIONS.TRANSACTION_VIEW) && {
      key: "Transaction",
      label: "Transaction",
      icon: <AiOutlineTransaction style={{ fontSize: 24 }} />,
      children: [
        hasPermission(PERMISSIONS.TRANSACTION_VIEW) && {
          key: "/transaction",
          label: "Transaction",
        },
      ].filter(Boolean),
    },

    hasPermission(PERMISSIONS.CAMPAIGN_WINNER_VIEW) && {
      key: "/CampaignWinner",
      label: "Winners",
      icon: <GiPodiumWinner style={{ fontSize: 24 }} />,
    },

     hasPermission(PERMISSIONS.AUDIT_REPORT_VIEW)&&{
      key: "/reporting",
      label: "Reporting",
      icon: <TbReport style={{ fontSize: 24 }} />,
    },

    hasPermission(PERMISSIONS.ACTIVITY_LOG_VIEW) && {
      key: "/Audit",
      label: "Audit",
      icon: <AiOutlineAudit style={{ fontSize: 24 }} />,
    },

    hasPermission(PERMISSIONS.TRX_UPLOAD_VIEW) && {
      key: "Upload",
      label: "Uploads",
      icon: <MdOutlineUploadFile style={{ fontSize: 24 }} />,
      children: [
        hasPermission(PERMISSIONS.TRX_UPLOAD_VIEW) && {
          key: "/upload",
          label: "Upload",
        },
        hasPermission(PERMISSIONS.TRANSACTION_VIEW) && {
          key: "/screenmaker",
          label: "Uploaded Data",
        },
      ].filter(Boolean),
    },
    //    hasPermission(PERMISSIONS.CAMPAIGN_WINNER_VIEW) && {
    //   key: "/screenmaker",
    //   label: "Makers",
    //   icon: <MdScreenshotMonitor style={{ fontSize: 24 }} />,
    // },

    hasPermission(PERMISSIONS.USER_VIEW) && {
      key: "/users",
      label: "Users",
      icon: <FaUserCircle style={{ fontSize: 24 }} />,
    },

    hasPermission(PERMISSIONS.ROLE_VIEW) && {
      key: "/roles",
      label: "Roles",
      icon: <VscUngroupByRefType style={{ fontSize: 24 }} />,
    },

    hasPermission(PERMISSIONS.CSV_TEMPLATE_VIEW)&&{
      key: "/templatemapping",
      label: "Template Mapping",
      icon: <BsFileEarmarkRuled style={{ fontSize: 24 }} />,

    },

    // hasPermission(PERMISSIONS.AD_CONFIG_VIEW)&&{
    //   key: "/adconfig",
    //   label: "AD Config",
    //   icon: <BsFileEarmarkRuled style={{ fontSize: 24 }} />,

    // },
    (hasPermission(PERMISSIONS.AD_CONFIG_VIEW) || hasPermission(PERMISSIONS.USER_CONFIG_VIEW) || hasPermission(PERMISSIONS.PASSWORD_POLICY_VIEW) || hasPermission(PERMISSIONS.NOTIFICATION_CONFIG_UPSERT) || hasPermission(PERMISSIONS.NOTIFICATION_CONFIG_VIEW)) && {
      key:"configuration",
      label:"Configuration",
      icon:<MdSettings style={{fontSize:24}} />,
      children:[
        hasPermission(PERMISSIONS.AD_CONFIG_VIEW) && {
          key: "/adconfig",
          label: "Ad Config",
          icon: <GrConfigure style={{ fontSize: 24 }} />,
        },
        hasPermission(PERMISSIONS.USER_CONFIG_VIEW) && {
          key: "/notifyconfig",
          label: "Notification Config",
          icon: <MdNotifications style={{ fontSize: 24 }} />,
        },
        hasPermission(PERMISSIONS.PASSWORD_POLICY_VIEW) && {
          key: "/password-policy",
          label: "Password Policy",
          icon: <MdSettings style={{ fontSize: 24 }} />,
        },
        (hasPermission(PERMISSIONS.NOTIFICATION_CONFIG_UPSERT) || hasPermission(PERMISSIONS.NOTIFICATION_CONFIG_VIEW)) && {
          key: "/smtp-config",
          label: "SMTP Config",
          icon: <MdNotifications style={{ fontSize: 24 }} />,
        },
      ].filter(Boolean)
    },

    hasPermission(PERMISSIONS.BLACKLIST_VIEW) && {
      key: "/blacklist",
      label: "Blacklisted Winners",
      icon: <GiPodiumWinner style={{ fontSize: 24 }} />,
    },

    hasPermission(PERMISSIONS.INSTITUTION_VIEW) && {
      key: "/institutions",
      label: "Institutions",
      icon: <MdBusiness style={{ fontSize: 24 }} />,
    },



  ]);


  return (
    <div className={style.Container} style={{ transition: "width 0.3s", height: "100vh", position: isMobile ? "absolute" : "relative", zIndex: 1000, }}>
      <div className={style.logoContainer}>
        <img alt="logo" src={Logo} className={`${style.LogoClose} ${collapsed ? style.collapsed : ''}`} width={collapsed ? 40 : 200} height={collapsed ? 20 : 100} />
      </div>
      <div className={style.sideList}>
        <Menu
          onClick={onClick}
          selectedKeys={[currentPath]}
          openKeys={collapsed ? [] : openKeys}
          onOpenChange={(keys) => setOpenKeys(keys)}
          mode="inline"
          theme="light"
          inlineCollapsed={collapsed}
          items={items}
          className={`${style.sideListMenu} ${style.custommenu}`}
        />
      </div>
    </div>
  );
};

export default Sider;
